import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { SalidaExterna } from '@core/routing/salida-externa.service';
import { Application } from '@domain/Application';
import { HttpResponse } from '@domain/HttpResponse';
import { PaymentResponseStatus } from '@domain/payment/PaymentReponseStatus';
import { environment } from '@env/environment';
import { ApplicationService } from '@services/application.service';
import { PaymentService, isPaymentApiOk } from '@services/payment.service';

/**
 * Lo que la pantalla de retorno hace hacia fuera (SPEC-013, Fase 3).
 *
 * Son dos cosas y ninguna mas: **anotar `payment.paymentStatus`** y **reabrir el
 * pago rechazado**. Vive aparte del componente por la misma razon que
 * `InicioDePago` en SPEC-012 — son viajes encadenados y la pantalla solo tiene
 * que saber si salieron o no.
 */
@Injectable({ providedIn: 'root' })
export class RetornoDePago {
  private readonly pagos = inject(PaymentService);
  private readonly solicitudes = inject(ApplicationService);
  private readonly salida = inject(SalidaExterna);

  /**
   * Anota en la solicitud lo ultimo que dijo el middleware. **Y nada mas**
   * (AC-11).
   *
   * `processStage` no se toca: los seis estados de cierre los escribe la Logic
   * App del pago, que ademas crea al alumno, la matricula y el cupo del grupo.
   * Escribirlo aqui seria competir con ella, o peor, decir *"matriculado"* sin
   * que exista matricula.
   *
   * **Si falla no se cuenta.** La pantalla ya esta diciendo el desenlace, que es
   * lo que la persona necesita; esto es una anotacion nuestra y no vale la pena
   * ensuciar el mensaje con su fracaso. La Logic App llega igual.
   *
   * Y no se escribe lo que ya esta escrito: volver a entrar con el pago resuelto
   * no genera una actualizacion (AC-12).
   */
  async anotar(solicitud: Application, estado: PaymentResponseStatus): Promise<void> {
    const pago = solicitud.payment;
    if (!pago || pago.paymentStatus === estado) return;

    const actualizada: Application = { ...solicitud, payment: { ...pago, paymentStatus: estado } };
    await firstValueFrom(this.solicitudes.update(actualizada)).catch(() => null);
  }

  /**
   * Reabre **el mismo pago** y vuelve al checkout (AC-9).
   *
   * `/retry` emite referencia y firma nuevas contra PayU sobre el `paymentId`
   * que ya existe. Crear un pago nuevo dejaria registros huerfanos y complicaria
   * la conciliacion — por eso el middleware tiene este endpoint, que hasta hoy
   * **no llamaba nadie**.
   *
   * Contra el doble cobro protege el middleware: un pago terminal no reabre el
   * checkout. Aqui no se reimplementa, se refleja.
   */
  async reintentar(paymentId: string): Promise<void> {
    const respuesta = (await firstValueFrom(
      this.pagos.retryPayment(paymentId),
    )) as HttpResponse | null;

    if (!respuesta || !isPaymentApiOk(respuesta.code)) throw new Error('Sin reintento');
    this.salida.ir(`${environment.urlPagos}/payment/checkout/${paymentId}`);
  }
}
